import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Button, Input } from '@/components/ui'
import { SEO } from '@/components/seo'

// ============================================================================
// QUIZ QUESTION POOL
// ============================================================================
interface QuizQuestion {
  id: string
  question: string
  options: string[]
  correct: number
}

const questionPool: QuizQuestion[] = [
  {
    id: 'q1',
    question: "What is the primary benefit of using WealthSpace's AI-powered reports?",
    options: [
      'They replace the need for financial advisers',
      'They save hours of manual report writing while maintaining compliance',
      'They automatically invest client money',
      'They generate random financial advice',
    ],
    correct: 1,
  },
  {
    id: 'q2',
    question: 'How does WealthSpace capture meeting information?',
    options: [
      'Manual note-taking only',
      'AI-powered transcription with automatic fact extraction',
      'Email summaries',
      'Client questionnaires only',
    ],
    correct: 1,
  },
  {
    id: 'q3',
    question: 'What compliance framework does WealthSpace help you follow?',
    options: [
      'US SEC regulations',
      'FCA Consumer Duty and UK financial regulations',
      'European MiFID only',
      'No specific compliance framework',
    ],
    correct: 1,
  },
  {
    id: 'q4',
    question: 'What is the recommended first step after logging into WealthSpace?',
    options: [
      'Immediately generate a report',
      'Delete all existing data',
      'Complete your firm profile and import existing clients',
      'Change the colour scheme',
    ],
    correct: 2,
  },
  {
    id: 'q5',
    question: 'How does the client portal benefit your practice?',
    options: [
      'It replaces all client communication',
      'Clients can securely view documents, track progress, and message you',
      'It automatically signs documents',
      'It provides investment advice to clients',
    ],
    correct: 1,
  },
  {
    id: 'q6',
    question: 'What happens when you record a client meeting in WealthSpace?',
    options: [
      'Nothing - recordings are just stored',
      'AI transcribes, extracts key facts, and suggests action items',
      'Recordings are sent directly to the FCA',
      'Meetings are broadcast live',
    ],
    correct: 1,
  },
  {
    id: 'q7',
    question: 'How does WealthSpace handle sensitive client data?',
    options: [
      'Data is stored on public servers',
      'All data is encrypted and stored in UK data centres with GDPR compliance',
      'Data is shared with third parties',
      'Data is not encrypted',
    ],
    correct: 1,
  },
  {
    id: 'q8',
    question: 'What type of reports can WealthSpace generate?',
    options: [
      'Only basic text documents',
      'Suitability reports, annual reviews, file notes, and client presentations',
      'Only spreadsheets',
      'Only compliance checklists',
    ],
    correct: 1,
  },
  {
    id: 'q9',
    question: "How can you get help if you're stuck in WealthSpace?",
    options: [
      'There is no support available',
      'Email only with 5-day response time',
      'In-app AI assistant, help articles, and quick support calls',
      'Post on social media',
    ],
    correct: 2,
  },
  {
    id: 'q10',
    question: 'What is the purpose of the compliance checker feature?',
    options: [
      'To automatically submit reports to the FCA',
      'To review documents against FCA guidelines and flag potential issues',
      'To replace your compliance officer',
      'To delete non-compliant documents',
    ],
    correct: 1,
  },
  {
    id: 'q11',
    question: 'How does WealthSpace integrate with your existing workflow?',
    options: [
      'It requires you to change everything',
      'It works alongside your existing tools with import/export capabilities',
      'It only works with one specific back-office system',
      "It doesn't integrate with anything",
    ],
    correct: 1,
  },
  {
    id: 'q12',
    question: 'What is included in the standard WealthSpace subscription?',
    options: [
      'Only report generation',
      'AI reports, meeting capture, client portal, compliance tools, and support',
      'Just data storage',
      'Only compliance checking',
    ],
    correct: 1,
  },
]

// Select random questions
function getRandomQuestions(count = 4): QuizQuestion[] {
  const shuffled = [...questionPool].sort(() => Math.random() - 0.5)
  return shuffled.slice(0, count)
}

// ============================================================================
// STEP INDICATOR
// ============================================================================
interface Step {
  id: string
  label: string
}

function StepIndicator({ currentStep, steps }: { currentStep: number; steps: Step[] }) {
  return (
    <div className="flex justify-between mb-10 relative">
      {steps.map((step, idx) => (
        <div key={step.id} className="flex flex-col items-center flex-1 relative">
          <div
            className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold mb-2 transition-all duration-300 ${
              idx < currentStep
                ? 'bg-gradient-to-br from-accent-teal to-emerald-500 border-accent-teal text-brand-dark'
                : idx === currentStep
                  ? 'border-2 border-accent-teal text-accent-teal bg-accent-teal/10'
                  : 'bg-white/5 border-2 border-white/10 text-brand-subtle'
            }`}
          >
            {idx < currentStep ? (
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : (
              idx + 1
            )}
          </div>
          <span className={`text-xs text-center ${idx <= currentStep ? 'text-brand-light' : 'text-brand-subtle'}`}>
            {step.label}
          </span>
          {idx < steps.length - 1 && (
            <div className="absolute top-[18px] left-[60%] w-[80%] h-0.5 bg-white/10" />
          )}
        </div>
      ))}
    </div>
  )
}

// ============================================================================
// VIDEO STEP
// ============================================================================
function VideoStep({ onComplete }: { onComplete: () => void }) {
  const [canProceed, setCanProceed] = useState(false)

  return (
    <div>
      <h2 className="font-serif text-2xl font-medium text-brand-light mb-2 text-center">
        Watch the Onboarding Video
      </h2>
      <p className="text-[15px] text-brand-muted text-center mb-8">
        This 15-minute video covers everything you need to get started with WealthSpace.
      </p>

      <div className="mb-6">
        {/* Video placeholder */}
        <div className="bg-black/30 rounded-xl aspect-video flex flex-col items-center justify-center gap-4 mb-3">
          <svg className="w-16 h-16 text-accent-teal" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <polygon points="5 3 19 12 5 21 5 3" />
          </svg>
          <p className="text-brand-muted text-center">
            Onboarding Video
            <br />
            <span className="text-sm opacity-70">15 minutes</span>
          </p>
          <button
            onClick={() => setCanProceed(true)}
            className="mt-4 px-4 py-2 bg-white/10 border border-white/20 rounded-md text-brand-muted text-xs hover:bg-white/15 transition-colors"
          >
            Skip to Quiz (Demo)
          </button>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-white/10 rounded-full mb-2">
          <div
            className="h-full bg-gradient-to-r from-accent-teal to-emerald-500 rounded-full transition-all duration-300"
            style={{ width: canProceed ? '100%' : '0%' }}
          />
        </div>
        <p className="text-[13px] text-brand-subtle text-center">
          {canProceed
            ? 'Video complete! You can now proceed.'
            : 'Watch at least 90% to continue'}
        </p>
      </div>

      <Button
        onClick={onComplete}
        disabled={!canProceed}
        className="w-full justify-center"
      >
        Continue to Quiz
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 18l6-6-6-6" />
        </svg>
      </Button>
    </div>
  )
}

// ============================================================================
// QUIZ STEP
// ============================================================================
function QuizStep({
  questions,
  onComplete,
  onFail,
}: {
  questions: QuizQuestion[]
  onComplete: () => void
  onFail: (score: number, total: number) => void
}) {
  const [currentQ, setCurrentQ] = useState(0)
  const [answers, setAnswers] = useState<Record<string, { selected: number; correct: boolean }>>({})
  const [selectedOption, setSelectedOption] = useState<number | null>(null)
  const [showResult, setShowResult] = useState(false)
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null)

  const question = questions[currentQ]

  const handleSelect = (optionIdx: number) => {
    if (showResult) return
    setSelectedOption(optionIdx)
  }

  const handleSubmit = () => {
    if (selectedOption === null) return
    const correct = selectedOption === question.correct
    setIsCorrect(correct)
    setShowResult(true)
    setAnswers({ ...answers, [question.id]: { selected: selectedOption, correct } })
  }

  const handleNext = () => {
    if (currentQ < questions.length - 1) {
      setCurrentQ(currentQ + 1)
      setSelectedOption(null)
      setShowResult(false)
      setIsCorrect(null)
    } else {
      const correctCount = Object.values(answers).filter((a) => a.correct).length + (isCorrect ? 1 : 0)
      const passingScore = Math.ceil(questions.length * 0.75)
      if (correctCount >= passingScore) {
        onComplete()
      } else {
        onFail(correctCount, questions.length)
      }
    }
  }

  return (
    <div>
      <h2 className="font-serif text-2xl font-medium text-brand-light mb-2 text-center">
        Quick Knowledge Check
      </h2>
      <p className="text-[15px] text-brand-muted text-center mb-8">
        Answer these questions to confirm you've understood the key concepts.
      </p>

      {/* Progress */}
      <div className="mb-6">
        <span className="text-sm text-brand-muted">
          Question {currentQ + 1} of {questions.length}
        </span>
        <div className="h-1 bg-white/10 rounded-full mt-2">
          <div
            className="h-full bg-gradient-to-r from-accent-teal to-emerald-500 rounded-full transition-all duration-300"
            style={{ width: `${((currentQ + 1) / questions.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Question card */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-6 mb-6">
        <p className="text-[17px] text-brand-light mb-5 leading-[1.5]">{question.question}</p>

        <div className="space-y-2.5 mb-5">
          {question.options.map((option, idx) => (
            <button
              key={idx}
              onClick={() => handleSelect(idx)}
              disabled={showResult}
              className={`w-full flex items-center gap-3 py-3.5 px-4 rounded-lg border text-left text-sm transition-all duration-200 ${
                showResult && idx === question.correct
                  ? 'border-emerald-500 bg-emerald-500/15 text-brand-light'
                  : showResult && selectedOption === idx && idx !== question.correct
                    ? 'border-red-500 bg-red-500/15 text-brand-light'
                    : selectedOption === idx
                      ? 'border-accent-teal bg-accent-teal/10 text-brand-light'
                      : 'border-white/10 bg-white/[0.03] text-brand-light hover:border-white/20'
              }`}
            >
              <span className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center text-[13px] font-semibold flex-shrink-0">
                {String.fromCharCode(65 + idx)}
              </span>
              <span className="flex-1">{option}</span>
              {showResult && idx === question.correct && (
                <svg className="w-5 h-5 text-emerald-400 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
              {showResult && selectedOption === idx && idx !== question.correct && (
                <svg className="w-5 h-5 text-red-400 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              )}
            </button>
          ))}
        </div>

        {showResult && (
          <div
            className={`py-3 px-4 rounded-lg text-sm font-medium text-center mb-4 ${
              isCorrect ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/10 text-red-300'
            }`}
          >
            {isCorrect ? 'Correct!' : "Not quite right, but let's continue."}
          </div>
        )}

        {!showResult ? (
          <Button onClick={handleSubmit} disabled={selectedOption === null} className="w-full justify-center">
            Submit Answer
          </Button>
        ) : (
          <Button onClick={handleNext} className="w-full justify-center">
            {currentQ < questions.length - 1 ? 'Next Question' : 'Complete Quiz'}
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </Button>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// FCA VERIFICATION STEP
// ============================================================================
interface FCAFormData {
  name: string
  email: string
  company: string
  fcaNumber: string
}

function FCAVerificationStep({ onComplete }: { onComplete: (data: FCAFormData) => void }) {
  const [form, setForm] = useState<FCAFormData>({
    name: '',
    email: '',
    company: '',
    fcaNumber: '',
  })
  const [errors, setErrors] = useState<Partial<FCAFormData>>({})
  const [isVerifying, setIsVerifying] = useState(false)

  const validate = () => {
    const errs: Partial<FCAFormData> = {}
    if (!form.name.trim()) errs.name = 'Name is required'
    if (!form.email.trim()) errs.email = 'Email is required'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Invalid email'
    if (!form.company.trim()) errs.company = 'Company name is required'
    if (!form.fcaNumber.trim()) errs.fcaNumber = 'FCA number is required'
    else if (!/^\d{6,7}$/.test(form.fcaNumber.trim())) errs.fcaNumber = 'FCA number should be 6-7 digits'
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return

    setIsVerifying(true)
    // Simulate verification
    await new Promise((resolve) => setTimeout(resolve, 1500))
    setIsVerifying(false)
    onComplete(form)
  }

  return (
    <div>
      <h2 className="font-serif text-2xl font-medium text-brand-light mb-2 text-center">
        Verify Your Details
      </h2>
      <p className="text-[15px] text-brand-muted text-center mb-8">
        Please provide your FCA registration number to verify your firm.
      </p>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="block text-[13px] font-medium text-brand-muted mb-1.5">Your name *</label>
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="John Smith"
            className={errors.name ? 'border-red-500' : ''}
          />
          {errors.name && <span className="text-xs text-red-400 mt-1">{errors.name}</span>}
        </div>

        <div>
          <label className="block text-[13px] font-medium text-brand-muted mb-1.5">Work email *</label>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="john@yourfirm.co.uk"
            className={errors.email ? 'border-red-500' : ''}
          />
          {errors.email && <span className="text-xs text-red-400 mt-1">{errors.email}</span>}
        </div>

        <div>
          <label className="block text-[13px] font-medium text-brand-muted mb-1.5">Firm name *</label>
          <Input
            value={form.company}
            onChange={(e) => setForm({ ...form, company: e.target.value })}
            placeholder="Smith Financial Planning Ltd"
            className={errors.company ? 'border-red-500' : ''}
          />
          {errors.company && <span className="text-xs text-red-400 mt-1">{errors.company}</span>}
        </div>

        <div>
          <label className="block text-[13px] font-medium text-brand-muted mb-1.5">
            FCA Firm Reference Number *
          </label>
          <Input
            value={form.fcaNumber}
            onChange={(e) => setForm({ ...form, fcaNumber: e.target.value })}
            placeholder="123456"
            className={errors.fcaNumber ? 'border-red-500' : ''}
          />
          {errors.fcaNumber && <span className="text-xs text-red-400 mt-1">{errors.fcaNumber}</span>}
          <p className="text-xs text-brand-subtle mt-1.5">
            Your 6 or 7 digit FCA reference number. Find it on the{' '}
            <a
              href="https://register.fca.org.uk/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent-teal hover:underline"
            >
              FCA Register
            </a>
          </p>
        </div>

        <Button type="submit" disabled={isVerifying} className="w-full justify-center">
          {isVerifying ? (
            <>
              <span className="w-4 h-4 border-2 border-brand-dark/30 border-t-brand-dark rounded-full animate-spin" />
              Verifying...
            </>
          ) : (
            <>
              Verify & Create Account
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </>
          )}
        </Button>
      </form>
    </div>
  )
}

// ============================================================================
// SUCCESS STEP
// ============================================================================
function SuccessStep({ userData }: { userData: FCAFormData }) {
  const [isCreating, setIsCreating] = useState(true)
  const [credentials, setCredentials] = useState<{ email: string; tempPassword: string } | null>(null)

  useState(() => {
    const timer = setTimeout(() => {
      setCredentials({
        email: userData.email,
        tempPassword: 'Welcome2026!',
      })
      setIsCreating(false)
    }, 2000)
    return () => clearTimeout(timer)
  })

  if (isCreating) {
    return (
      <div className="text-center py-10">
        <div className="w-12 h-12 border-3 border-accent-teal/20 border-t-accent-teal rounded-full animate-spin mx-auto mb-6" />
        <h2 className="font-serif text-2xl font-medium text-brand-light mb-2">Creating Your Account</h2>
        <p className="text-[15px] text-brand-muted">This will only take a moment...</p>
      </div>
    )
  }

  return (
    <div className="text-center">
      <div className="mb-6">
        <svg className="w-16 h-16 text-accent-teal mx-auto" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
      </div>

      <h2 className="font-serif text-[28px] font-medium text-brand-light mb-2">Welcome to WealthSpace!</h2>
      <p className="text-[15px] text-brand-muted mb-8">Your account is ready. Here are your login details:</p>

      <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-5 text-left mb-6">
        <div className="flex justify-between items-center py-3 border-b border-white/[0.06]">
          <span className="text-[13px] text-brand-subtle">Email</span>
          <span className="text-sm text-brand-light">{credentials?.email}</span>
        </div>
        <div className="flex justify-between items-center py-3 border-b border-white/[0.06]">
          <span className="text-[13px] text-brand-subtle">Temporary Password</span>
          <code className="text-sm text-accent-teal bg-accent-teal/10 px-2 py-1 rounded font-mono">
            {credentials?.tempPassword}
          </code>
        </div>
        <p className="text-xs text-brand-subtle mt-3 italic">
          You'll be asked to set a new password when you first log in.
        </p>
      </div>

      <a
        href="https://app.wealthspace.co.uk"
        className="inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-accent-teal to-emerald-500 rounded-lg text-brand-dark font-semibold text-[15px] hover:shadow-lg hover:shadow-accent-teal/25 transition-all"
      >
        Log In to WealthSpace
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M5 12h14M12 5l7 7-7 7" />
        </svg>
      </a>

      <p className="text-[13px] text-brand-subtle mt-5">
        We've also sent these details to <strong>{credentials?.email}</strong>
      </p>
    </div>
  )
}

// ============================================================================
// QUIZ FAILED STEP
// ============================================================================
function QuizFailedStep({
  score,
  total,
  onRetry,
}: {
  score: number
  total: number
  onRetry: () => void
}) {
  return (
    <div className="text-center py-5">
      <div className="mb-5">
        <svg className="w-12 h-12 text-amber-500 mx-auto" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      </div>

      <h2 className="font-serif text-2xl font-medium text-brand-light mb-2">Let's Review the Video</h2>
      <p className="text-[15px] text-amber-400 mb-3">
        You got {score} out of {total} questions correct. You need at least {Math.ceil(total * 0.75)} to proceed.
      </p>
      <p className="text-sm text-brand-muted mb-8 max-w-[400px] mx-auto leading-relaxed">
        The video contains important information that will help you get the most out of WealthSpace. Please
        watch it again and retry the quiz.
      </p>

      <Button onClick={onRetry}>
        Watch Video Again
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M1 4v6h6" />
          <path d="M23 20v-6h-6" />
          <path d="M20.49 9A9 9 0 005.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 013.51 15" />
        </svg>
      </Button>
    </div>
  )
}

// ============================================================================
// MAIN PAGE
// ============================================================================
export default function BookInstantStartPage() {
  const [step, setStep] = useState(0) // 0: video, 1: quiz, 2: fca, 3: success
  const quizQuestions = useMemo(() => getRandomQuestions(4), [])
  const [userData, setUserData] = useState<FCAFormData | null>(null)
  const [quizFailed, setQuizFailed] = useState<{ score: number; total: number } | null>(null)

  const steps: Step[] = [
    { id: 'video', label: 'Watch Video' },
    { id: 'quiz', label: 'Quiz' },
    { id: 'verify', label: 'Verify' },
    { id: 'access', label: 'Access' },
  ]

  const handleVideoComplete = () => setStep(1)

  const handleQuizComplete = () => {
    setQuizFailed(null)
    setStep(2)
  }

  const handleQuizFail = (score: number, total: number) => {
    setQuizFailed({ score, total })
  }

  const handleRetry = () => {
    setQuizFailed(null)
    setStep(0)
  }

  const handleFCAComplete = (data: FCAFormData) => {
    setUserData(data)
    setStep(3)
  }

  return (
    <>
      <SEO
        title="Instant Start - WealthSpace"
        description="Get started with WealthSpace in 20 minutes. Watch our onboarding video, complete a quick quiz, and get instant access."
        keywords="WealthSpace instant start, self-service onboarding, financial adviser software trial"
        url="/book-instant-start"
      />

      {/* Hero */}
      <section className="pt-[60px] pb-10 px-6 max-w-[800px] mx-auto text-center">
        <p className="text-sm font-semibold text-accent-teal uppercase tracking-[0.1em] mb-4">
          Self-service onboarding
        </p>
        <h1 className="font-serif text-[clamp(2rem,5vw,3rem)] font-normal leading-[1.2] text-brand-light mb-4">
          <span className="text-accent-teal">Instant</span> Start
        </h1>
        <p className="text-[17px] text-brand-muted max-w-[500px] mx-auto">
          Watch our quick onboarding video, answer a few questions, and get instant access to WealthSpace.
        </p>
      </section>

      {/* Widget */}
      <section className="px-6 pb-20">
        <div className="max-w-[640px] mx-auto">
          <div className="bg-[linear-gradient(145deg,rgba(255,255,255,0.03)_0%,rgba(255,255,255,0.01)_100%)] border border-white/[0.06] rounded-2xl p-8">
            <StepIndicator currentStep={step} steps={steps} />

            {step === 0 && !quizFailed && <VideoStep onComplete={handleVideoComplete} />}

            {step === 1 && !quizFailed && (
              <QuizStep questions={quizQuestions} onComplete={handleQuizComplete} onFail={handleQuizFail} />
            )}

            {quizFailed && <QuizFailedStep score={quizFailed.score} total={quizFailed.total} onRetry={handleRetry} />}

            {step === 2 && <FCAVerificationStep onComplete={handleFCAComplete} />}

            {step === 3 && userData && <SuccessStep userData={userData} />}
          </div>
        </div>
      </section>

      {/* Back Link */}
      <div className="text-center pt-10 pb-10 px-6">
        <Link
          to="/get-started"
          className="inline-flex items-center gap-1.5 text-sm text-brand-subtle hover:text-brand-muted transition-colors"
        >
          <svg className="w-4 h-4 opacity-70" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to options
        </Link>
      </div>
    </>
  )
}
